/**
 * The SIP Radius gateway path as a signal flow: encoders push into the gateway,
 * TM dials it back per stack, each stack writes HLS to S3.
 *
 * Shared by the Test lab and by a live game's resource card, because the lab is
 * a rehearsal of exactly this pipeline — same gateway, same caller-mode stacks,
 * same egress. Both callers pass their own feed table and their own view of the
 * gateway; nothing below knows which one it is drawing.
 *
 * Lanes come from the caller's feed table, not from what TM happens to be
 * running, so a camera nobody has launched still gets a row — an absent lane
 * would read as a render that failed rather than as a feed nobody has started.
 */

import { useState } from 'react';
import {
  Box,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';

import { isTmStackActive, resolveTmStack, type TmStackLike } from '../tmStacks';
import {
  FlowConnector,
  FlowModuleChips,
  FlowNodeCard,
  FlowStatusBadge,
  FlowTile,
  MONO_FONT,
  SignalFlowGrid,
  flowToken,
  type FlowColumn,
  type FlowLane,
  type FlowModule,
  type FlowTone,
} from './signal-flow';

/** The stages, in pipeline order. Toggling one drops it and its connector. */
const MODULES: FlowModule[] = [
  { key: 'encoders', label: 'Encoders' },
  { key: 'gateway', label: 'Gateway' },
  { key: 'stacks', label: 'TM stacks' },
  { key: 'output', label: 'HLS output' },
];

/**
 * EC2's gateway states, as tones.
 *
 * `unknown` is idle rather than an error: it means the read did not resolve, and
 * `gatewayError` beside the diagram already says so in words.
 */
const GATEWAY_TONES: Record<string, FlowTone> = {
  stopped: 'idle',
  unknown: 'idle',
  pending: 'pending',
  stopping: 'pending',
  running: 'active',
  unreachable: 'error',
};

/**
 * TM's own stack states, as tones.
 *
 * `error` must not read like `deploying` — surfacing a per-feed failure is the
 * point of this panel — so anything in flight that TM names falls through to
 * pending rather than sharing a colour with a dead stack.
 */
const STACK_TONES: Record<string, FlowTone> = {
  running: 'active',
  error: 'error',
  inactive: 'idle',
  // Not a TM state; see `statusForFeed`. A run that ended, as against a feed TM
  // has never launched.
  finished: 'idle',
};

/**
 * One feed's two gateway ports.
 *
 * Declared structurally rather than imported from either caller's response
 * type: the Test lab and a live game answer the same three fields, and binding
 * the panel to one of them is what would force the other to fake it.
 */
export interface FlowFeed {
  feed: string;
  /** Gateway port an encoder pushes into. */
  inputPort: number;
  /** Gateway port the TM stack dials. */
  outputPort: number;
  /**
   * This feed is in a launch the caller has queued but not yet run, so TM has
   * never been told about it. Optional because only a live game has a queue
   * behind it — the Test lab launches on the spot and leaves this unset.
   */
  planned?: boolean;
}

/** One TM config, joined to a feed. `TmStackLike` carries the rest. */
export interface FlowStack extends TmStackLike {
  feed: string;
  egressBucket: string | null;
  egressPrefix: string | null;
}

interface Lane extends FlowLane {
  feed: FlowFeed;
  /** TM's state, or null when TM has no config for this feed at all. */
  state: string | null;
  tone: FlowTone;
  failure: string | null;
  /** TM has something up for this feed that a /stop would act on. */
  stoppable: boolean;
  /** TM holds a config for this feed and nothing is running from it. */
  startable: boolean;
  egressBucket: string | null;
  egressPrefix: string | null;
}

interface GatewaySignalFlowProps {
  hostname: string;
  gatewayState: string;
  gatewayIpAddress: string | null;
  instanceId: string | null;
  feeds: FlowFeed[];
  stacks: FlowStack[];
  /**
   * Shut this one feed's stack down.
   *
   * Optional, and absent is the meaningful default: the Test lab tears the
   * whole lab down in a single action and has no per-feed stop, so leaving
   * this unset is what keeps the panel read-only there.
   */
  onStopStack?: (feed: string) => void;
  /** The feed with a stop in flight, so only its own button shows the wait. */
  stoppingFeed?: string | null;
  /**
   * Start this one feed's stack again from the config it already has. Absent
   * for the same reason as `onStopStack`, and also while the game is in a state
   * the endpoint refuses.
   */
  onStartStack?: (feed: string) => void;
  startingFeed?: string | null;
}

export default function GatewaySignalFlow({
  hostname,
  gatewayState,
  gatewayIpAddress,
  instanceId,
  feeds,
  stacks,
  onStopStack,
  stoppingFeed = null,
  onStartStack,
  startingFeed = null,
}: GatewaySignalFlowProps) {
  const theme = useTheme();
  const [enabled, setEnabled] = useState<string[]>(
    MODULES.map((module) => module.key),
  );

  // Membership only — the columns are assembled in pipeline order below, so the
  // order keys arrive in never reaches the layout.
  const toggle = (key: string) =>
    setEnabled((current) =>
      current.includes(key)
        ? current.filter((entry) => entry !== key)
        : [...current, key],
    );

  /**
   * One lane: the feed's ports from the mapping, and TM's answer for it.
   *
   * Egress is read off the same entry `resolveTmStack` chose, not looked up
   * separately — with duplicate config names a second search could pair one
   * config's state with another's output path.
   */
  const laneFor = (feed: FlowFeed): Lane => {
    const { entry, state, failure } = resolveTmStack(
      stacks.filter((stack) => stack.feed === feed.feed),
    );

    return {
      key: feed.feed,
      feed,
      state,
      tone: state ? STACK_TONES[state] ?? 'pending' : 'idle',
      failure,
      // Read off the chosen entry's own state rather than `state` above: that
      // one reports an ended run as `finished`, and isTmStackActive only knows
      // TM's vocabulary, so it would read that synthetic word as active. The
      // uuid is required too — there is no stack to stop without one.
      stoppable: isTmStackActive(entry?.state) && Boolean(entry?.stackUuid),
      // `inactive` only: `unknown` is a row no poll has read yet, which may well
      // be running.
      startable: entry?.state === 'inactive',
      egressBucket: entry?.egressBucket ?? null,
      egressPrefix: entry?.egressPrefix ?? null,
    };
  };

  const lanes = feeds.map(laneFor);
  const gatewayTone = GATEWAY_TONES[gatewayState] ?? 'idle';

  const columns: Array<FlowColumn<Lane>> = [];

  if (enabled.includes('encoders')) {
    columns.push({
      key: 'encoders',
      title: 'Encoders',
      width: 'auto',
      cell: {
        span: 'lane',
        render: (lane) => (
          <FlowTile
            sx={{
              px: 1.5,
              py: 1,
              display: 'grid',
              placeItems: 'center',
              flex: 'none',
              minWidth: 64,
            }}
          >
            <Typography
              variant="caption"
              sx={{ fontFamily: MONO_FONT, color: flowToken(theme, 'wash200') }}
            >
              {lane.feed.feed}
            </Typography>
          </FlowTile>
        ),
      },
    });
    columns.push({
      key: 'encoders-link',
      width: 'minmax(88px, 1fr)',
      cell: {
        span: 'lane',
        // An encoder pushing is something we cannot observe — nothing reports
        // back from the far side of the socket — so this line tracks the lane it
        // feeds rather than claiming knowledge of its own.
        render: (lane) => (
          <FlowConnector
            label={`push :${lane.feed.inputPort}`}
            tone={lane.tone}
          />
        ),
      },
    });
  }

  if (enabled.includes('gateway')) {
    columns.push({
      key: 'gateway',
      title: 'Gateway',
      width: 'minmax(210px, auto)',
      cell: {
        span: 'lanes',
        render: () => (
          <FlowNodeCard
            title="SIP Radius GW"
            tone={gatewayTone}
            pulse={gatewayTone === 'pending'}
            statusLabel={gatewayState.replace(/_/g, ' ')}
            lines={[hostname, instanceId].filter(Boolean)}
            note="Allocates one socket pair per stack and hands the endpoints to TM."
          >
            {gatewayIpAddress && (
              <Stack direction="row" sx={{ alignItems: 'center', gap: 0.5 }}>
                <Typography
                  variant="caption"
                  sx={{
                    fontFamily: MONO_FONT,
                    color: flowToken(theme, 'wash200'),
                  }}
                >
                  {gatewayIpAddress}
                </Typography>
                <Tooltip title="Copy gateway IP">
                  <IconButton
                    size="small"
                    onClick={() =>
                      navigator.clipboard?.writeText(gatewayIpAddress)
                    }
                  >
                    <ContentCopyIcon fontSize="inherit" />
                  </IconButton>
                </Tooltip>
              </Stack>
            )}
          </FlowNodeCard>
        ),
      },
    });
    columns.push({
      key: 'gateway-link',
      width: 'minmax(96px, 1fr)',
      cell: {
        span: 'lane',
        // Animated while the stack is mid-transition: TM dialling the gateway is
        // exactly what `pending` means here, and a settled socket is better read
        // as a solid line than a moving one.
        render: (lane) => (
          <FlowConnector
            label={`TM dials :${lane.feed.outputPort}`}
            tone={lane.tone}
            animated={lane.tone === 'pending'}
          />
        ),
      },
    });
  }

  if (enabled.includes('stacks')) {
    columns.push({
      key: 'stacks',
      title: 'TiledMedia stacks',
      width: 'minmax(200px, 1.2fr)',
      cell: {
        span: 'lane',
        render: (lane) => (
          <FlowTile>
            <Stack
              direction="row"
              sx={{
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 0.5,
              }}
            >
              <Typography
                variant="caption"
                sx={{ fontFamily: MONO_FONT, fontWeight: 500 }}
              >
                TM · {lane.feed.feed}
              </Typography>
              {/* Only when the caller offered a stop, and only on a lane with
                  something to stop: on a dead feed the endpoint answers 409,
                  and a button that exists to be refused reads as broken.
                  A stopped feed with a config gets a start in its place. */}
              {onStartStack && lane.startable ? (
                <Tooltip title={`Start the ${lane.feed.feed} stack`}>
                  <span>
                    <IconButton
                      size="small"
                      color="success"
                      aria-label={`Start the ${lane.feed.feed} stack`}
                      disabled={startingFeed === lane.feed.feed}
                      onClick={() => onStartStack(lane.feed.feed)}
                    >
                      <PlayArrowIcon fontSize="inherit" />
                    </IconButton>
                  </span>
                </Tooltip>
              ) : onStopStack && (
                <Tooltip
                  title={
                    lane.stoppable
                      ? `Shut down the ${lane.feed.feed} stack`
                      : 'Nothing running for this feed'
                  }
                >
                  {/* A disabled button emits no events, so Tooltip needs a
                      wrapper it can hover — otherwise the explanation for why
                      the button is dead is itself unreachable. */}
                  <span>
                    <IconButton
                      size="small"
                      color="error"
                      aria-label={`Shut down the ${lane.feed.feed} stack`}
                      disabled={
                        !lane.stoppable || stoppingFeed === lane.feed.feed
                      }
                      onClick={() => onStopStack(lane.feed.feed)}
                    >
                      <PowerSettingsNewIcon fontSize="inherit" />
                    </IconButton>
                  </span>
                </Tooltip>
              )}
            </Stack>
            {/* Always a state pill. "N/A" is real information — TM has never
                seen this feed — but as blank space it reads as a broken render.

                `planned` is the same absence with a reason: TM has no config
                because the launch is still sitting in the queue. Worth its own
                word, because "N/A" on a feed that *is* scheduled is exactly
                how a queued pgm came to look like a pgm nobody had scheduled.
                Still the idle tone: nothing is running either way. */}
            <FlowStatusBadge
              label={lane.state ?? (lane.feed.planned ? 'planned' : 'N/A')}
              tone={lane.tone}
              // Same rule as the gateway card and the connector beside it:
              // pending is the only tone with a transition still in flight.
              pulse={lane.tone === 'pending'}
              sx={{ mt: 0.5 }}
            />

            {/* Inline, not a tooltip: this is the reason the feed is dead, it is
                long, it is worth copying into a ticket, and a hover target
                nobody knows to hover is how you end up reading TM's /list by
                hand. The grid has no fixed row height, so the lane simply grows
                and stays aligned with its connectors. */}
            {lane.failure && (
              <Box sx={{ mt: 1 }}>
                <Typography
                  variant="caption"
                  sx={{
                    display: 'block',
                    color: flowToken(theme, 'red500'),
                    fontWeight: 600,
                  }}
                >
                  Last deployment failure
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    display: 'block',
                    color: flowToken(theme, 'red500'),
                    fontFamily: MONO_FONT,
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                  }}
                >
                  {lane.failure}
                </Typography>
              </Box>
            )}
          </FlowTile>
        ),
      },
    });
  }

  if (enabled.includes('output')) {
    if (enabled.includes('stacks')) {
      columns.push({
        key: 'output-link',
        width: 'minmax(88px, 1fr)',
        cell: {
          span: 'lane',
          // Marching only while the stack is up, because this is the one hop we
          // can speak to: a running stack is writing segments.
          render: (lane) => (
            <FlowConnector
              label="writes HLS"
              tone={lane.tone}
              animated={lane.tone === 'active'}
            />
          ),
        },
      });
    }

    columns.push({
      key: 'output',
      title: 'HLS output',
      width: 'minmax(210px, 1.2fr)',
      cell: {
        span: 'lane',
        render: (lane) => (
          <FlowTile>
            <Typography
              variant="caption"
              sx={{
                fontFamily: MONO_FONT,
                display: 'block',
                color: flowToken(theme, 'wash200'),
                overflowWrap: 'break-word',
              }}
            >
              {lane.egressPrefix
                ? `s3://${lane.egressBucket}/${lane.egressPrefix}/`
                : 'no egress configured'}
            </Typography>
            {lane.egressPrefix && (
              <Typography
                variant="caption"
                sx={{
                  fontFamily: MONO_FONT,
                  display: 'block',
                  mt: 0.375,
                  color: flowToken(theme, 'wash500'),
                }}
              >
                feed-0/master.m3u8
              </Typography>
            )}
          </FlowTile>
        ),
      },
    });
  }

  return (
    <Stack spacing={2}>
      <FlowModuleChips modules={MODULES} enabled={enabled} onToggle={toggle} />
      {columns.length === 0 ? (
        <Typography variant="body2" sx={{ color: flowToken(theme, 'wash500') }}>
          Every stage is hidden — add one above to see the flow.
        </Typography>
      ) : (
        <SignalFlowGrid columns={columns} lanes={lanes} />
      )}
    </Stack>
  );
}
